import { useMount } from "ahooks"
import { Spin, Tag, Descriptions, Skeleton, Button, Divider, Modal, Form, Result, message, InputNumber } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import { PageHeader } from '@ant-design/pro-layout';
import React, { useMemo } from "react"
import { BillCredit, BillItem, BillModes, BillPayment, BillType } from "../../constants/bill"
import { isComplate, ItemNames } from "../../constants/serve"
import Requestor from "../../requestor"
import dayjs from "dayjs"

type IProps = {
  sid: string
  onComplete: () => void
}

export const payTag = (status: number) => {
  if (status === 0) {
    return <Tag color="processing">待支付</Tag>
  }

  return <Tag color="success">已支付</Tag>
}

export const creditTag = (status: number) => {
  if (status === 0) {
    return <Tag color="processing">待挂账</Tag>
  }

  return <Tag color="success">已挂账</Tag>
}



const BillItems = ({ sid }: { sid: string }) => {

  const [items, setItems] = React.useState<BillItem[]>([])


  // 查询账单信息
  const getBillItems = React.useCallback(async (sid: string) => {

    try {
      let res = await Requestor.getBillItems<BillItem[]>(sid);

      if (res.error) {
        throw new Error(res.error)
      }

      setItems(res.result)

    }
    catch (e) {
      message.error("查询服务项目失败：" + e)
    }
  }, [])

  useMount(() => { getBillItems(sid) })

  return <div>
    {items.map(it => (<Descriptions column={2} key={it.id} title={ItemNames.get(it.itemCode)}>
      {it.startingStr && <Descriptions.Item label="起始地">{it.startingStr}</Descriptions.Item>}
      {it.distance && <Descriptions.Item label="导航距离">{Math.ceil(it.distance / 1000)}公里</Descriptions.Item>}
      {it.destinationStr && <Descriptions.Item label="送达地">{it.destinationStr}</Descriptions.Item>}
      {it.remark && <Descriptions.Item label="说明">{it.remark}</Descriptions.Item>}
      <Descriptions.Item label="服务金额">¥{it.total / 100}</Descriptions.Item>
    </Descriptions>))}
  </div>
}


const Bill = ({ sid }: IProps) => {
  const [spin, setSpin] = React.useState<boolean>(false)
  const [isEdit, setEdit] = React.useState<boolean>(false)
  const [isEditSp, setEditSp] = React.useState<boolean>(false)
  const [bills, setBill] = React.useState<{ bill: BillType | null, billPayment: BillPayment | null, billCredit: BillCredit | null }>({ bill: null, billCredit: null, billPayment: null })
  const [form] = Form.useForm()

  // 查询账单信息
  const getBills = React.useCallback(async (sid: string) => {
    setSpin(true)
    try {
      let bill_res = await Requestor.getBill<BillType>(sid);
      let bill_pay_res = await Requestor.getBillPayment<BillPayment | null>(sid);
      let bill_credit_res = await Requestor.getBillCredit<BillCredit | null>(sid);

      /* if (bill_res.error) {
        throw new Error(bill_res.error)
      }

      if (bill_pay_res.error) {
        throw new Error(bill_pay_res.error)
      }

      if (bill_credit_res.error) {
        throw new Error(bill_credit_res.error)
      }
 */
      setBill({ bill: bill_res.result, billPayment: bill_pay_res.result, billCredit: bill_credit_res.result })

    } catch (error) {
      message.error("查询账单失败：" + error)
    } finally {
      setSpin(false)
    }

  }, [])


  // 初始化请求数据
  useMount(() => {
    getBills(sid);
  })

  const _validator = () => ({
    validator({ field }: any, value: string) {

      // 不处理没有值的情况 其他规则处理这部分的问题
      if (value === undefined || value === null) {
        return Promise.resolve();
      }

      if (bill === null) {
        return Promise.reject(new Error("请稍后再操作"));
        return;
      }
      // 单位 元 
      let max = bill.totalFee / 100
      let nowValue = Number(value)

      if (nowValue > max) {
        return Promise.reject(new Error("该值不能超过总金额"));
      }

      // 保证总金额一致 增加支付金额则对应的挂账金额减少
      if (field === 'creditFee') {
        form.setFieldsValue({ payFee: max - nowValue })
      } else {
        form.setFieldsValue({ creditFee: max - nowValue })
      }
      return Promise.resolve();
    },
  })

  const { bill, billPayment, billCredit } = useMemo(() => { return bills }, [bills])


  if (bills === null || bill === null) {
    return <Result icon={<LoadingOutlined />} title={"正在查询，请稍后"}></Result>
  }

  return <Spin spinning={spin} tip="正在加载">
    <div className="background-color-f0f0f0">
      <PageHeader title="服务账单" extra={[<Button key="12312" type="primary">总金额: ¥{bill.totalFee / 100}</Button>,
        // 已经完结的单不能修改
        // <Button key="sas6" disabled={bill.payStatus === 1 || bill.creditStatus === 1} danger onClick={setEdit.bind(this, true)}>修改付款金额比例</Button>
      ]}>
        <Skeleton loading={sid === null} active>
          <Descriptions>
            <Descriptions.Item label="报案号">
              {bill.outNo}
            </Descriptions.Item>
            <Descriptions.Item label="车牌号码">
              {bill.license}
            </Descriptions.Item>
            <Descriptions.Item label="付款模式">
              {BillModes[bill.mode]}
            </Descriptions.Item>
            <Descriptions.Item label="支付状态">
              {bill && (isComplate({ paymentMode: bill.mode, payStatus: bill.payStatus, creditStatus: bill.creditStatus }) ? <Tag color="green">已完成</Tag> : <Tag color="red">未完成</Tag>)}
            </Descriptions.Item>
            {bill.creditFee !== 0 && <Descriptions.Item label="挂账状态"> ¥{bill.creditFee / 100}&nbsp;
              {creditTag(bill.creditStatus)}
            </Descriptions.Item>}
            {bill.payFee !== 0 && <Descriptions.Item label="支付状态">¥{bill.payFee / 100}&nbsp;
              {payTag(bill.payStatus)}
            </Descriptions.Item>}
          </Descriptions>
        </Skeleton>
      </PageHeader>
    </div>
    <PageHeader title="账单详情">
      {billPayment && billPayment.status === 1 && <Descriptions title="支付详情">
        <Descriptions.Item label="支付金额">¥{billPayment.totalFee / 100}</Descriptions.Item>
        <Descriptions.Item label="支付时间">{dayjs(billPayment.startedAt * 1000).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>
        <Descriptions.Item label="订单编号">{billPayment.outTradeNo}</Descriptions.Item>
        <Descriptions.Item label="支付编号">{billPayment.transactionId}</Descriptions.Item>
      </Descriptions>}
      {billCredit && <Descriptions title="挂账详情">
        <Descriptions.Item label="挂账金额">¥{billCredit.totalFee / 100}</Descriptions.Item>
        <Descriptions.Item label="挂账时间">{dayjs(billCredit.createdAt * 1000).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>
      </Descriptions>}
    </PageHeader>
    <Divider />
    <div style={{ minHeight: "10vh" }}>
      <PageHeader title="服务项目">
        <BillItems sid={sid}></BillItems>
      </PageHeader>
    </div>
    <Modal open={isEdit} confirmLoading={isEditSp} destroyOnClose title="修改支付金额比例" okText="提交" maskClosable={false} onOk={() => {
      Modal.confirm({
        title: "确认修改支付金额比例吗",
        content: "修改支付金额比例后，将不会使用该公司的统一付款规则，请慎重操作",
        okText: "确认修改",
        onOk: () => {
          form.submit()
        },
        okType: "danger",
      })
    }} onCancel={setEdit.bind(this, false)}>
      <Form layout="horizontal" form={form} initialValues={{ payFee: bill.payFee / 100, creditFee: bill.creditFee / 100 }}>
        <Form.Item label="挂账金额" name="creditFee" required rules={[{ required: true, message: "请输入挂账金额" }, _validator]}>
          <InputNumber style={{ width: '100%' }} addonAfter="元"></InputNumber>
        </Form.Item>
        <Form.Item label="支付金额" name="payFee" required rules={[{ required: true, message: "请输入支付金额" }, _validator]}>
          <InputNumber style={{ width: '100%' }} addonAfter="元"></InputNumber>
        </Form.Item>
      </Form>
    </Modal>
  </Spin>
}


export default Bill